var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { leftIcon, rightIcon } from './icon';
export var Button = AwsUi.__addComponent('awsui-button', {
    releaseStatus: 'stable',
    properties: {
        // Renders the button as disabled and prevents clicks.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // Renders the button as being in a loading state. It takes precendence over
        // the `disabled` if both are set to `true`. It prevents clicks.
        loading: {
            type: 'boolean'
        },
        // Displays an icon next to the text. Check the `iconAlign` property for positioning.
        icon: {
            type: 'string',
            // FIXME: Get list of icons from AWS-UI-Styles package
            valid: [
                'add-plus',
                'angle-down-double',
                'angle-down',
                'angle-left-double',
                'angle-left',
                'angle-right-double',
                'angle-right',
                'angle-up-double',
                'angle-up',
                'bug',
                'call',
                'calendar',
                'caret-down-filled',
                'caret-down',
                'caret-left-filled',
                'caret-left',
                'caret-right-filled',
                'caret-right',
                'caret-up-filled',
                'caret-up',
                'contact',
                'close',
                'copy',
                'download',
                'edit',
                'envelope',
                'expand',
                'external',
                'file-open',
                'file',
                'filter',
                'folder-open',
                'folder',
                'heart',
                'help',
                'key',
                'lock-private',
                'menu',
                'microphone',
                'notification',
                'refresh',
                'search',
                'settings',
                'share',
                'status-in-progress',
                'status-info',
                'status-negative',
                'status-pending',
                'status-positive',
                'status-stopped',
                'status-warning',
                'treeview-collapse',
                'treeview-expand',
                'undo',
                'unlocked',
                'upload',
                'view-full',
                'view-horizontal',
                'view-vertical',
                'zoom-in',
                'zoom-out'
            ]
        },
        // URL to a custom icon image. It should be used instead of the `icon` property
        // if the desired icon is not available.
        iconUrl: {
            type: 'string'
        },
        // Alternate text for the custom icon, please provide this for accessibility compliance.
        // This property has an effect only when the `iconUrl` property is set.
        // Default icons already have predefined names.
        iconAlt: {
            type: 'string'
        },
        iconAlign: {
            type: 'string',
            nullable: false,
            defaultValue: 'left',
            valid: ['left', 'right']
        },
        // The form action that is performed by a button click.
        formAction: {
            type: 'string',
            nullable: false,
            defaultValue: 'submit',
            valid: ['submit', 'none']
        },
        // This property will be used as `aria-label`. It should be used in buttons
        // that don't have text in order to make them accessible.
        label: {
            type: 'string'
        },
        // Renders a link with button styling. Use this property if you need a link
        // styled as a button (`variant=link`), for example when you have a 'help' button that links
        // to the documentation.
        href: {
            type: 'string'
        },
        // When using the `href` property to render a button as link, you can add
        // a target attribute to specify the target of the link (example: `_blank`).
        target: {
            type: 'string'
        },
        // When set to `false` will prevent the `text` content from wrapping.
        // Defaults to `true`.
        wrapText: {
            type: 'boolean',
            defaultValue: true
        },
        // Determines the general styling of the button. Use
        // * `primary` for primary buttons,
        // * `normal` for secondary buttons,
        // * `link` for tertiary buttons,
        // * `icon` to only display the icon without text.
        variant: {
            nullable: false,
            type: 'string',
            valid: ['normal', 'primary', 'link', 'icon'],
            defaultValue: 'normal'
        },
        __tabindex: {
            type: 'integer'
        },
        // Used by ButtonDropdown to indicate the expanded state.
        __ariaExpanded: {
            type: 'string'
        },
        // Used by ButtonDropdown to provide `aria-haspopup`
        __ariaHasPopup: {
            type: 'string'
        }
    },
    functions: {
        // Focuses the underlying native button.
        focus: function () {
            var button = this.node.querySelector('button');
            if (button) {
                button.focus();
            }
        },
        __isDisabled: function () {
            return this.loading || this.disabled;
        }
    },
    events: {
        // Fired when the user clicks on the button, and the button is not disabled or in loading state.
        //
        // **AngularJS:** `ng-click` works incorrectly, you need to use `click` attribute
        click: {
            cancelable: true,
            detailType: 'Button.ClickDetail'
        }
    },
    regions: {
        // Text displayed in the button element.
        text: {
            isDefault: true
        }
    },
    render: function (m, h) {
        var _this = this;
        var isAnchor = Boolean(this.href);
        var text = this.variant === 'icon' ? '' : h.region('text', 'span') || '';
        var buttonClass = new LegacyClassBuilder(this)
            .addClass('awsui-button')
            .addPropertyValueClass('variant')
            .addClass('awsui-hover-child-icons')
            .addClass('awsui-button-no-text', !text)
            .addClass('awsui-button-disabled', this.__isDisabled())
            .addClass('awsui-button-no-wrap', !this.wrapText)
            .toClassName();
        var clickHandler = function (nativeEvent) {
            if (isAnchor && _this.__isDisabled()) {
                return nativeEvent.preventDefault();
            }
            var button = nativeEvent.button, ctrlKey = nativeEvent.ctrlKey, shiftKey = nativeEvent.shiftKey, altKey = nativeEvent.altKey, metaKey = nativeEvent.metaKey;
            _this.__fireEvent('click', { button: button, ctrlKey: ctrlKey, shiftKey: shiftKey, altKey: altKey, metaKey: metaKey }, {
                nativeEvent: nativeEvent
            });
        };
        var buttonContent = [leftIcon(this), text, rightIcon(this)];
        var buttonType = this.formAction === 'none' ? 'button' : 'submit';
        var attributes = h.copyDefined({
            'aria-label': this.label,
            className: buttonClass,
            onclick: clickHandler,
            type: isAnchor ? undefined : buttonType,
            'override-focus': true
        });
        var anchorAttributes = h.copyDefined(__assign(__assign({}, attributes), { href: this.href, target: this.target, 
            // security recommendation: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#target
            rel: this.target === '_blank' ? 'noopener' : undefined, tabIndex: this.__isDisabled() ? -1 : this.__tabindex, 'aria-disabled': this.__isDisabled() ? 'true' : undefined }));
        var buttonAttributes = h.copyDefined(__assign(__assign({}, attributes), { tabIndex: this.__tabindex, disabled: this.__isDisabled() ? true : undefined, 'aria-expanded': this.__ariaExpanded, 'aria-haspopup': this.__ariaHasPopup }));
        return isAnchor ? (AwsUi.__createElement("a", __assign({}, anchorAttributes), buttonContent)) : (AwsUi.__createElement("button", __assign({}, buttonAttributes), buttonContent));
    }
});
//# sourceMappingURL=button.js.map